import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO =
    "https://customer-assets.emergentagent.com/job_luxury-facilitation/artifacts/6k1jx2uv_MP%20LOGO%20NEW.png";

const KEY = "monplaisir:intro-shown";

export default function IntroOverlay() {
    // Show once per session. On reload within the same tab session it stays hidden.
    const [show, setShow] = useState(() => {
        if (typeof window === "undefined") return false;
        try {
            return sessionStorage.getItem(KEY) !== "1";
        } catch {
            return true;
        }
    });

    useEffect(() => {
        if (!show) return;
        try {
            sessionStorage.setItem(KEY, "1");
        } catch {}
        // Lock body scroll while intro is on screen
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const t = setTimeout(() => {
            setShow(false);
            document.body.style.overflow = prev;
        }, 3600);
        return () => {
            clearTimeout(t);
            document.body.style.overflow = prev;
        };
    }, [show]);

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    key="intro"
                    data-testid="intro-overlay"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
                    onClick={() => setShow(false)}
                    className="fixed inset-0 z-[100] bg-[#0a0a0a] flex items-center justify-center cursor-pointer overflow-hidden"
                >
                    {/* Subtle vignette */}
                    <div className="absolute inset-0 bg-radial-gradient pointer-events-none"
                         style={{
                             background:
                                 "radial-gradient(ellipse at center, rgba(94,25,20,0.18) 0%, rgba(10,10,10,0) 55%)",
                         }}
                    />

                    {/* Overline */}
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.2 }}
                        className="absolute top-1/2 -translate-y-[180px] md:-translate-y-[230px] flex items-center gap-3 text-[#E5C7A1]"
                    >
                        <span className="h-px w-10 bg-[#C5A880]" />
                        <span className="font-sans text-[10px] uppercase tracking-[0.45em]">
                            Bespoke · Discreet · Worldwide
                        </span>
                        <span className="h-px w-10 bg-[#C5A880]" />
                    </motion.div>

                    {/* Logo */}
                    <motion.img
                        src={LOGO}
                        alt="Monplaisir Group"
                        initial={{ opacity: 0, scale: 0.94, filter: "blur(6px)" }}
                        animate={{
                            opacity: [0, 1, 1, 0],
                            scale: [0.94, 1, 1.02, 1.06],
                            filter: [
                                "blur(6px)",
                                "blur(0px)",
                                "blur(0px)",
                                "blur(2px)",
                            ],
                        }}
                        transition={{
                            duration: 3.4,
                            times: [0, 0.28, 0.78, 1],
                            ease: [0.2, 0.7, 0.2, 1],
                        }}
                        className="relative z-10 h-[40vh] max-h-[440px] w-auto"
                    />

                    {/* Slow ring sweep */}
                    <motion.div
                        initial={{ scale: 0.7, opacity: 0 }}
                        animate={{ scale: 2.4, opacity: [0, 0.18, 0] }}
                        transition={{ duration: 3.2, ease: "easeOut", delay: 0.2 }}
                        className="absolute w-[420px] h-[420px] rounded-full border border-[#C5A880]/40 pointer-events-none"
                    />

                    {/* Skip hint */}
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.4 }}
                        transition={{ duration: 0.8, delay: 1.6 }}
                        className="absolute bottom-10 font-sans text-[10px] uppercase tracking-[0.4em] text-[#F9F8F6]/40"
                    >
                        Tap anywhere to enter
                    </motion.span>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
